/**
 * Compliance — chunk 4.
 *
 * Contract mirror: labourcore-api/src/modules/compliance/.
 *
 * ── Why this module is held to a higher standard ──────────────────────────
 *
 * These tables hold identity documents — right-to-work evidence and
 * qualifications — hanging off `operatives`, which carries encrypted national
 * insurance numbers, dates of birth and bank details. A scoping mistake in the
 * deals module leaks a day rate. A scoping mistake here leaks someone's
 * passport. Every endpoint below validates the company against memberships
 * before returning a row, and the parity script asserts the cross-tenant case
 * on every one of them rather than spot-checking.
 *
 * ── What starts working ───────────────────────────────────────────────────
 *
 * 1. THE DOCUMENTS LIST shows whose certificate it is. `operative:operative_id
 *    (full_name, trade)` was dropped by the proxy on every load.
 * 2. THE QUALIFICATIONS CARD explains itself. Both
 *    `governing_body:governing_body_id(...)` and
 *    `level:qualification_level_id(...)` were dropped — so a card titled
 *    "Qualifications & credentials" has never shown which body awards a
 *    qualification, nor the plain-English explanation of what it means.
 * 3. SHARE LINKS ARE SCOPED. Revoking ran `UPDATE … WHERE id = $1` on a table
 *    with RLS disabled — anyone who learned a token id could kill another
 *    company's public compliance page. The identical hole was closed on score
 *    tokens in chunk 3; the fix did not travel.
 * 4. IDENTITY COMES FROM THE TOKEN. `uploaded_by`, `created_by_user_id` and
 *    `requested_by_user_id` were all read from the browser's own session.
 *
 * ── What is deliberately NOT decided ──────────────────────────────────────
 *
 * WHICH company a multi-company user's wallet should show. That is PD-1 — the
 * company is still the caller's choice, and the server only refuses one they do
 * not belong to. Proposal: Product-KT/08-Decision-Acting-Context.md.
 */

import { apiGet, apiPost } from "./client";

export type Row = Record<string, unknown>;

export interface VerifiedStatus {
  company_id: string;
  manual_count: number;
  ai_clean_count: number;
  /** The value the badge actually reads. */
  verified: boolean;
}

/** One operative's documents. Scoped to the caller's companies server-side. */
export function fetchOperativeDocuments(operativeId: string): Promise<Row[]> {
  return apiGet<Row[]>(`/api/compliance/operatives/${operativeId}/documents`);
}

/**
 * A company's verified headline.
 *
 * Replaces three browser reads: two counts, plus a third that pulled up to 200
 * rows to filter `ai_flags` in JavaScript because the proxy could not express
 * an array predicate. One statement now, and no cap.
 */
export function fetchVerifiedStatus(companyId: string): Promise<VerifiedStatus> {
  return apiGet<VerifiedStatus>("/api/compliance/verified-status", {
    company_id: companyId,
  });
}

/** A company's documents, each with the operative it belongs to. */
export function fetchComplianceDocuments(companyId: string): Promise<Row[]> {
  return apiGet<Row[]>("/api/compliance/documents", { company_id: companyId });
}

export interface RecordDocumentBody {
  company_id: string;
  operative_id?: string | null;
  file_name: string;
  file_path: string;
  file_size?: number | null;
  mime_type?: string | null;
  cert_label?: string | null;
  cert_kind?: string | null;
  expires_at?: string | null;
  sha256?: string | null;
}

/**
 * Record a document that has already been uploaded to storage.
 *
 * The FILE upload itself stays on `.storage` — that was always a named
 * operation and is out of scope. Only the row moves.
 *
 * `uploaded_by` is NOT part of this body and cannot be.
 */
export function recordComplianceDocument(body: RecordDocumentBody): Promise<Row> {
  return apiPost<Row>("/api/compliance/documents", body);
}

/* ── Public share links ─────────────────────────────────────────────────── */

export interface ComplianceToken extends Row {
  id: string;
  company_id: string;
  token: string;
  label: string | null;
  revoked_at: string | null;
}

export function fetchComplianceTokens(): Promise<ComplianceToken[]> {
  return apiGet<ComplianceToken[]>("/api/compliance/tokens");
}

/** The token value comes from the database default, never the client. */
export function createComplianceToken(
  companyId: string,
  label?: string | null,
): Promise<ComplianceToken> {
  return apiPost<ComplianceToken>("/api/compliance/tokens", {
    company_id: companyId,
    label: label ?? null,
  });
}

/** 404 — not 403 — when the token is not the caller's, so it cannot confirm one exists. */
export function revokeComplianceToken(tokenId: string): Promise<{ revoked: boolean }> {
  return apiPost<{ revoked: boolean }>(`/api/compliance/tokens/${tokenId}/revoke`, {});
}

/* ── Qualifications ─────────────────────────────────────────────────────── */

export interface QualificationsBundle {
  held: Row[];
  trade_bodies: Row[];
  levels: Row[];
}

/**
 * Qualifications for an operative or a company.
 *
 * The subject chooses the table — `operative_credentials` or
 * `company_accreditations`. The call site picked it with a variable, which made
 * it invisible to any search for a literal table name; the server maps it
 * explicitly and rejects anything outside the map.
 */
export function fetchQualifications(opts: {
  subject: "operative" | "company";
  subjectId: string;
  trades?: string[];
  disciplines?: string[];
}): Promise<QualificationsBundle> {
  const params: Record<string, string> = {
    subject: opts.subject,
    subject_id: opts.subjectId,
  };
  if (opts.trades?.length) params.trades = opts.trades.join(",");
  if (opts.disciplines?.length) params.disciplines = opts.disciplines.join(",");
  return apiGet<QualificationsBundle>("/api/compliance/qualifications", params);
}

/* ── Requests ───────────────────────────────────────────────────────────── */

/** `requested_by_user_id` is the authenticated subject, not part of this body. */
export function createComplianceRequest(body: {
  coordinator_company_id: string;
  partner_company_id: string;
  cert_kind: string;
  cert_label?: string | null;
  notes?: string | null;
}): Promise<Row> {
  return apiPost<Row>("/api/compliance/requests", body);
}

/**
 * Decline a request.
 *
 * Found during the migration, not on the chunk inventory: a third write on
 * `compliance_requests` scoped by row id alone.
 */
export function declineComplianceRequest(
  requestId: string,
  reason?: string | null,
): Promise<Row> {
  return apiPost<Row>(`/api/compliance/requests/${requestId}/decline`, {
    reason: reason ?? null,
  });
}

export function fulfilComplianceRequest(
  requestId: string,
  documentId?: string | null,
): Promise<Row> {
  return apiPost<Row>(`/api/compliance/requests/${requestId}/fulfil`, {
    document_id: documentId ?? null,
  });
}

/* ── NDA panel ──────────────────────────────────────────────────────────── */

export interface NdaPanelBundle {
  requires_nda: boolean;
  documents: Row[];
}

/**
 * NDA gate state and every revision, in one call.
 *
 * Three browser reads before — the parent's `requires_nda`, then all NDA rows —
 * with the ownership check left to row-level security, which is disabled on
 * these tables. The endpoint checks it explicitly.
 */
export function fetchNdaPanel(
  kind: "project" | "opportunity",
  scopeId: string,
): Promise<NdaPanelBundle> {
  return apiGet<NdaPanelBundle>("/api/compliance/nda", { kind, scope_id: scopeId });
}

export function setNdaGate(
  kind: "project" | "opportunity",
  scopeId: string,
  requiresNda: boolean,
): Promise<{ requires_nda: boolean }> {
  return apiPost<{ requires_nda: boolean }>(
    `/api/compliance/nda/gate?kind=${kind}&scope_id=${scopeId}`,
    { requires_nda: requiresNda },
  );
}
